/*
 * File: KeyDealsCard.tsx
 * Project: a-a
 * File Created: Monday, 6th January 2025 7:52:47 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Tuesday, 9th December 2025 11:48:41 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2025 Mutual Mobile, Mutual Mobile
 */
import React from 'react';

import zeplinColor from '../../../../../theme/Color';
import { Box, Chip, Divider, Skeleton, Typography, useTheme } from '@mui/material';

import { LightTooltip } from '../../../CustomTooltip/CustomTooltip';
import styles from './KeyDealsCard.module.css';
import { useLocation } from 'react-router-dom';
type DealType = {
  dealId: string | null;
  accountId: string;
  name: string;
  accountName: string;
  stageName: string;
  amount: string | null;
  clientRevenue: string | null;
  companyRevenue: string | null;
  commitments: string | null;
  closeDate: string | null;
  lastModifiedByName: string | null;
  lastModifiedDate: string | null;
  createdByName: string | null;
  createdDate: string | null;
  ownerName: string | null;
  probability: number | null;
  type: string | null;
  iqScore: number | null;
};
const KeyDealsCard = ({
  data,
  selected,
  loading = false,
  index,
  setValue,
}: {
  data: DealType;
  selected?: DealType | undefined;
  loading?: boolean;
  index?: number;
  setValue?: React.Dispatch<React.SetStateAction<number>> | undefined;
}) => {
  const theme = useTheme();
  const location = useLocation();
  return (
    <Box
      onClick={() => {
        setValue && setValue(3);
        sessionStorage.setItem('dealSelectedFromOverview', (data?.dealId || '0').toString());
        sessionStorage.setItem('currentTab', '3');
      }}
      sx={{
        // borderRadius: '14px',
        // overflowX: 'hidden',
        padding: { xs: '10px', xl: '24px' },
        // margin: 'auto',
        // height: '93px',
        backgroundColor: zeplinColor.Neutral1,
        borderWidth: `1px 1px 1px 1px`,
        borderStyle: 'solid',
        borderColor: `${
          data?.dealId === selected?.dealId ? zeplinColor?.BasicBlack : zeplinColor.Neutral5
        }`,
        borderRadius: '14px',

        // alignItems: 'center',
        cursor: 'pointer',
        width: '100%',
        '&:hover': { borderColor: zeplinColor?.BasicBlack },
        // justifyContent: 'flex-start',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          // flexDirection: 'column',
          flex: 1,

          alignItems: 'flex-start',
          gap: { xs: '6px', xl: '12px' },
        }}
      >
        {loading ? (
          <Skeleton variant='rounded' width={32} height={32} />
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // flex: 1,
              padding: '6px',
              borderRadius: '8px',
              border: `1px solid ${zeplinColor.Neutral5}`,
              // width: '32px',
              // height: '32px',
            }}
          >
            <img
              src='/assets/no-keydeals.svg'
              className={styles['deal-icon']}
              style={{ objectFit: 'fill' }}
            />
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexDirection: 'column',
            flex: 1,
            gap: { xs: '6px', xl: '8px' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              // flexDirection: 'column',
              // alignItems: 'flex-start',
              width: '100%',
              justifyContent: 'space-between',
              gap: { xs: '6px', xl: '12px' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                // justifyContent: 'space-between',
                // alignItems: 'flex-start',
                // flexDirection: 'column',
                // gap: { xs: '6px', xl: '8px' },
                flex: 4,
                width: '100%', // Ensure this box takes full width within its container
                overflow: 'hidden', // Prevent content from overflowing
              }}
            >
              {loading ? (
                <Skeleton
                  variant='text'
                  sx={{
                    fontSize: theme.typography.body2,
                    // Optionally adjust other styles here
                  }}
                />
              ) : (
                <LightTooltip
                  title={
                    <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                      {data?.name ?? 'N/A'}
                    </Typography>
                  }
                  placement='bottom-start'
                  style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                >
                  <Typography
                    variant='responsiveHeading4'
                    sx={{
                      display: 'block',
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      // maxWidth: '100%',
                      maxWidth: { xs: '13rem', xl: '18rem' }, // Adjust width for responsiveness
                      // width: {
                      //   xs: '150px',
                      //   xl: '200px',
                      // },
                    }}
                  >
                    {data?.name ?? 'N/A'}
                  </Typography>
                </LightTooltip>
              )}
            </Box>
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  // Optionally adjust other styles here
                }}
              />
            ) : (
              <Chip
                label={
                  <Typography
                    variant='responsiveLabel1'
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    {data?.stageName ?? 'NA'}
                  </Typography>
                }
                sx={{
                  bgcolor: (() => {
                    switch (data?.stageName) {
                      case 'Tracking':
                        return zeplinColor.Neutral8;
                      case 'Contacted':
                        return zeplinColor.Blue;
                      case 'Determine Budget':
                        return zeplinColor.Blue5;
                      case 'Agreement Presented':
                        return zeplinColor.Yellow2;
                      case 'Review':
                        return zeplinColor.Brand1;
                      case 'Closed Won':
                      case 'Signed Contract':
                        return zeplinColor.Success;

                      case 'Closed Lost':
                        return zeplinColor.Error;
                      default:
                        return zeplinColor.Blue;
                    }
                  })(),
                  // Constants.PriorityTask[(data?.priority || 'Medium') as 'Normal' | 'Low' | 'High'],
                  color: zeplinColor.Neutral1,
                  // width: '39px',
                  // textAlign: 'left',
                  '&.MuiChip-root': {
                    height: '22px',
                    padding: '4px 8px',
                    borderRadius: '50px',

                    // margin: 0,
                    // flex: 1,
                  },
                  '& .MuiChip-label': {
                    padding: 0,
                  },
                }}
              />
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap', // Allow wrapping on small screens
              width: '100%', // Take full width
              overflow: 'hidden', // Prevent overflow
            }}
          >
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  width: '100%',
                }}
              />
            ) : (
              <>
                <Typography
                  variant='responsiveBody3'
                  color={zeplinColor.Neutral8}
                  sx={{
                    textWrap: 'nowrap',
                    overflow: 'hidden',
                    width: {
                      xs: '150px',
                      xl: '180px',
                    },
                    textOverflow: 'ellipsis',
                  }}
                >
                  Close date:{' '}
                  <Typography
                    variant='responsiveBody3'
                    color={zeplinColor.BasicBlack}
                    component='span'
                    sx={{
                      textWrap: 'nowrap',
                      overflow: 'hidden',

                      textOverflow: 'ellipsis',
                    }}
                  >
                    {data?.closeDate ?? 'N/A'}
                  </Typography>
                </Typography>
                {!location?.pathname?.includes('huddle') && (
                  <>
                    {' '}
                    <Divider
                      component='div'
                      orientation='vertical'
                      sx={{
                        borderColor: zeplinColor.Neutral5,
                        borderWidth: 1,
                        height: '20px',
                        display: { xs: 'none', sm: 'block' }, // Hide divider on very small screens
                      }}
                    />
                    <Typography
                      variant='responsiveBody3'
                      color={zeplinColor.Neutral8}
                      sx={{
                        textWrap: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        width: {
                          xs: '150px',
                          xl: '180px',
                        },
                      }}
                    >
                      Opp Score:{' '}
                      <Typography
                        variant='responsiveBody3'
                        color={zeplinColor.BasicBlack}
                        component='span'
                        sx={{
                          textWrap: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {data?.iqScore ?? 'N/A'}
                      </Typography>
                    </Typography>
                  </>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default KeyDealsCard;
